/* eslint-disable @typescript-eslint/no-unused-vars */
import { AbstractControl, FormControl, ValidationErrors, Validators, FormArray, FormGroup } from '@angular/forms';
import { differenceInYears } from 'date-fns';
import { Observable, timer } from 'rxjs';
import { map } from 'rxjs/operators';

import { WebFormFormGroup } from '../data/deal/web-form-form-group';
import { countRequiredSquareMeters } from '../pages/calculator/calculator-util';
import { incomeTypes, parseFormattedNumber, partnerIncomeTypes } from '../utils/form';

import { AvailableCalculatorControls } from './calculator-base-form';
import { DynamicForm } from './dynamic-form-builder.service';

import {
	companyTaxIdPattern,
	drivingLicencePattern,
	emailPattern,
	foreignDocumentPattern,
	idCardPattern,
	idNumberPattern,
	mobilePhoneWithPrefixPattern,
	passportPattern,
	personalIdPattern,
	hungarianPostCodePattern,
	ownershipSharePattern,
} from 'shared/constants/patterns';
import {
	CustomerType,
	GoalOfLoan,
	IdCardType,
	LanguageSkillLevel,
	MaritalStatus,
	ProductType,
	PropertyLocationQuestion,
	PropertyType,
	WebformDto,
	GoalOfProperty,
	PublicSpaceType,
	DateValidatorOption,
	NatureOfEmployment,
	GoalOfApplication,
} from 'shared/openapi/models';
import { createUTCDateWithoutOffset, createISODateStringWithoutTime } from 'shared/tools/date';

function extractNumerator(share: string): number {
	const numPart = share.substring(0, share.indexOf('/'));
	const num = parseInt(numPart, 10);
	return isNaN(num) ? 0 : num;
}

function extractDenominator(share: string): string {
	const idx = share.indexOf('/');
	return idx !== -1 ? share.substring(idx + 1) : '';
}

function allSharesHaveSameDenominator(shares: (string | null)[]): boolean {
	const validShares = shares.filter((s) => s?.includes('/')) as string[];
	if (validShares.length === 0) return true;
	const reference = extractDenominator(validShares[0]);
	return validShares.every((s) => extractDenominator(s) === reference);
}

function isNumeratorSumExceeded(shares: (string | null)[]): boolean {
	const validShares = shares.filter((s) => s?.includes('/')) as string[];
	if (validShares.length === 0) return false;

	const sum = validShares.reduce((acc, s) => acc + extractNumerator(s!), 0);
	const denominator = parseInt(extractDenominator(validShares[0]), 10);
	return !isNaN(denominator) && sum > denominator;
}

export const MinMaxBigDecimal =
	(productTypes: ProductType[], form: AvailableCalculatorControls | FormControl, min: string, max: string) =>
	(control: AbstractControl<number | string | null>) => {
		const value = typeof control.value === 'string' ? parseFormattedNumber(control.value) : control.value;
		if (
			value !== null &&
			value !== undefined &&
			control.value !== '' &&
			(isNaN(value) || value < parseInt(min) || value > parseInt(max))
		) {
			return { minMaxNumber: { productTypes, min, max } };
		}
		return null;
	};

export const MinMaxMonths =
	(
		productTypes: ProductType[],
		form: AvailableCalculatorControls | DynamicForm<WebFormFormGroup> | null,
		min: string,
		max: string
	) =>
	(control: AbstractControl<number | string | null>) => {
		const value = typeof control.value === 'string' ? parseFormattedNumber(control.value) : control.value;
		if (
			value !== null &&
			control.value !== '' &&
			(isNaN(value) || value < parseInt(min) || value > parseInt(max))
		) {
			const minInYears = Math.ceil(parseInt(min) / 12);
			const maxInYears = Math.floor(parseInt(max) / 12);
			return { minMaxMonths: { productTypes, minInYears, maxInYears } };
		}
		return null;
	};

export const EmailValidator = () => (control: AbstractControl<string | null>) => {
	const email = control.value;

	if (!email) return null;
	if (!emailPattern.test(email)) return { emailValidator: true };
	if (email.length > 254) return { emailValidator: true };

	const atIndex = email.lastIndexOf('@');
	const local = email.substring(0, atIndex);
	const domain = email.substring(atIndex + 1);

	if (atIndex === -1) return { emailValidator: true };
	if (local.length > 64) return { emailValidator: true };
	if (local.startsWith('.') || local.endsWith('.')) return { emailValidator: true };
	if (local.includes('..')) return { emailValidator: true };
	if (domain.split('.').some((label) => label.length > 63)) return { emailValidator: true };

	return null;
};

export const MinMaxDate =
	(productTypes: ProductType[], form: AvailableCalculatorControls | null, minDate: string, maxDate: string) =>
	(control: AbstractControl) => {
		const value = control.value;
		const minDateValue = createUTCDateWithoutOffset(new Date(minDate));

		let maxDateValue = null;
		if (maxDate === DateValidatorOption.Today) {
			maxDateValue = createUTCDateWithoutOffset(new Date());
		} else {
			maxDateValue = maxDate ? createUTCDateWithoutOffset(new Date(maxDate)) : null;
		}

		const date = createUTCDateWithoutOffset(new Date(value));

		if (!value) {
			return null;
		}

		if (maxDate === DateValidatorOption.Today && maxDateValue && maxDateValue < date) {
			return { minMaxDate: { productTypes, minDateValue, maxDateValue } };
		}

		if (maxDateValue && maxDateValue < date) {
			return { minMaxDate: { productTypes, minDateValue, maxDateValue } };
		}

		if (minDateValue > date) {
			return { minMaxDate: { productTypes, minDateValue, maxDateValue } };
		}

		return null;
	};

export const AppValidators = {
	RoundedTenThousand:
		(productTypes: ProductType[], form: AvailableCalculatorControls) =>
		(control: AbstractControl<number | string | null>) => {
			const value =
				typeof control.value === 'string' ? parseFloat(control.value.replace(/\s/g, '')) : control.value;
			if (value !== null && value % 10_000 !== 0) {
				return { roundedTenThousand: { productTypes } };
			}
			return null;
		},

	MinMaxNumber:
		(productTypes: ProductType[], form: AvailableCalculatorControls, min: string, max: string) =>
		(control: AbstractControl<number | string | null>) => {
			const value = typeof control.value === 'string' ? parseFormattedNumber(control.value) : control.value;
			if (
				value !== null &&
				value !== undefined &&
				control.value !== '' &&
				(isNaN(value) || value < parseFloat(min) || value > parseFloat(max))
			) {
				return { minMaxNumber: { productTypes, min, max } };
			}
			return null;
		},

	MinMaxBigDecimal,

	ZipCodeValidator:
		(productTypes: ProductType[], form: DynamicForm<WebFormFormGroup>) =>
		(control: AbstractControl<string | null>) => {
			if (!control.value) {
				return null;
			}
			return valueMatches(control.value, hungarianPostCodePattern)
				? null
				: { zipCodeValidator: { productTypes } };
		},

	OwnershipShareFormatValidator:
		(productTypes: ProductType[], form: DynamicForm<WebFormFormGroup>) =>
		(control: AbstractControl<string | null>) =>
			!control.value || valueMatches(control.value, ownershipSharePattern)
				? null
				: { ownershipShareFormatValidator: { productTypes } },

	OwnershipShareValueValidator: (productTypes: ProductType[], form: DynamicForm<WebFormFormGroup>) => () => {
		const ownerShares = (form.get('propertyData.owners') as FormArray).controls.map(
			(owner) => owner.get('ownershipShare')?.value
		);

		if (!allSharesHaveSameDenominator(ownerShares)) {
			return { ownershipShareDenominatorValidator: { productTypes } };
		}
		if (isNumeratorSumExceeded(ownerShares)) {
			return { ownershipShareNumeratorSumValidator: { productTypes } };
		}
		return null;
	},

	SoldSharedPropertyOwnershipShareValueValidator:
		(productTypes: ProductType[], form: DynamicForm<WebFormFormGroup>) => () => {
			const properties = form.get('propertyData.soldSharedProperties') as FormArray;

			for (const property of properties.controls) {
				const ownerShares = (property.get('soldSharedPropertyOwners') as FormArray).controls.map(
					(owner) => owner.get('ownershipShare')?.value
				);

				if (!allSharesHaveSameDenominator(ownerShares)) {
					return { ownershipShareDenominatorValidator: { productTypes } };
				}
				if (isNumeratorSumExceeded(ownerShares)) {
					return { ownershipShareNumeratorSumValidator: { productTypes } };
				}
			}
			return null;
		},

	Required: (productTypes: ProductType[]) => (control: AbstractControl) => {
		return Validators.required(control) ? { required: { productTypes } } : null;
	},

	RequiredTrue: (productTypes: ProductType[]) => (control: AbstractControl) => {
		return Validators.requiredTrue(control) ? { requiredTrue: { productTypes } } : null;
	},

	RequiredFalse: (productTypes: ProductType[]) => (control: AbstractControl) => {
		const translationPrefix = getControlPath(control);
		if (control.value !== false) {
			return { requiredFalse: { productTypes, translationPrefix } };
		}
		return null;
	},

	EmployerStartDateValidator:
		(productTypes: ProductType[], form: AvailableCalculatorControls, minAge: number, maxAge: number) =>
		(control: AbstractControl) => {
			const natureOfEmployment: NatureOfEmployment = control.parent?.get('natureOfEmployment')?.value;
			const today = createISODateStringWithoutTime(new Date());
			const startDateOfEmployment = createISODateStringWithoutTime(new Date(control.value));
			if (natureOfEmployment === NatureOfEmployment.Fixed && startDateOfEmployment > today) {
				return { employerStartDateValidator: { productTypes } };
			}
			return null;
		},

	EmployerEndDateValidator:
		(productTypes: ProductType[], form: AvailableCalculatorControls, minAge: number, maxAge: number) =>
		(control: AbstractControl) => {
			const natureOfEmployment: NatureOfEmployment = control.parent?.get('natureOfEmployment')?.value;
			const startDateOfEmployment = createISODateStringWithoutTime(
				new Date(control.parent?.get('employmentStartDate')?.value)
			);
			const endDateOfEmployment = createISODateStringWithoutTime(new Date(control.value));
			if (natureOfEmployment === NatureOfEmployment.Fixed && endDateOfEmployment < startDateOfEmployment) {
				return { employerEndDateValidator: { productTypes } };
			}
			return null;
		},

	MinMaxDate,

	MinMaxAge:
		(productTypes: ProductType[], form: AvailableCalculatorControls, minAge: number, maxAge: number) =>
		(control: AbstractControl) => {
			if (!control.value) {
				return null;
			}
			const age = differenceInYears(new Date(), new Date(control.value));

			if (age < minAge || age >= maxAge) {
				return { minMaxAge: { productTypes, minAge, maxAge } };
			}
			return null;
		},

	MinMaxItems:
		(productTypes: ProductType[], form: AvailableCalculatorControls, min: number, max: number) =>
		(control: AbstractControl) => {
			if (control.value && (control.value.length < min || control.value.length > max)) {
				return { minMaxItems: { productTypes, min, max } };
			}
			return null;
		},

	MinMaxOwners:
		(productTypes: ProductType[], form: AvailableCalculatorControls, min: number, max: number) =>
		(control: AbstractControl) => {
			if (control.value && (control.value.length < min || control.value.length > max)) {
				return { minMaxOwners: { productTypes, min, max } };
			}
			return null;
		},

	SharedPropertiesMinMaxItems:
		(productTypes: ProductType[], form: DynamicForm<WebFormFormGroup>, min: number, max: number) =>
		(control: AbstractControl<string | null>) => {
			const soldSharedProperties = control.parent?.get('soldSharedProperties')?.value ?? [];
			const sharedProperties = control.parent?.get('sharedProperties')?.value ?? [];
			const numberOfSharedProperties = soldSharedProperties.length + sharedProperties.length;
			if (control.value && (numberOfSharedProperties < min || numberOfSharedProperties > max)) {
				return { sharedPropertiesMinMaxItems: { productTypes, min, max } };
			}
			return null;
		},

	PurchasePriceOfPlannedProperty:
		(productTypes: ProductType[], form: AvailableCalculatorControls) => (control: AbstractControl) => {
			const value =
				typeof control.value === 'string' ? parseFloat(control.value.replace(/\s/g, '')) : control.value;
			const isFirstSharedProperty = form.get('propertyData.firstSharedProperty')?.value;
			const isPurchasePriceKnown = control.parent?.get('isPurchasePriceOfPlannedPropertyKnown')?.value;
			let min = 1_000_000;
			let max = 150_000_000;
			const goalOfApplication = form.controls.propertyData?.controls.goalOfApplication.value as GoalOfApplication;

			const maxLimitOfGoalApplications: Partial<Record<GoalOfApplication, number>> = {
				[GoalOfApplication.NewFlatBuying]: 100_000_000,
				[GoalOfApplication.UsedFlatBuying]: 100_000_000,
				[GoalOfApplication.UsedHouseBuying]: 150_000_000,
				[GoalOfApplication.NewHouseConstruction]: 150_000_000,
				[GoalOfApplication.NewHouseBuying]: 150_000_000,
				[GoalOfApplication.OwnHouseExpansion]: 150_000_000,
				[GoalOfApplication.UsedHouseBuyingAndExpansion]: 150_000_000,
			};

			if (isPurchasePriceKnown === false) {
				min = 0;
				max = 0;
			}

			const fail = () => ({ purchasePriceOfPlannedProperty: { productTypes, value: max } });
			const failMin = () => ({ minMaxNumber: { productTypes, min, max } });

			if (
				(productTypes.includes(ProductType.CsokPlusz) && isFirstSharedProperty === true) ||
				productTypes.includes(ProductType.OtthonStart)
			) {
				max = maxLimitOfGoalApplications[goalOfApplication]!;
			}

			if (value > max) {
				return fail();
			}

			if (value < min) {
				return failMin();
			}

			return null;
		},

	DealPurchasePriceOfPlannedProperty:
		(productTypes: ProductType[], form: DynamicForm<WebFormFormGroup>) => (control: AbstractControl) => {
			const value =
				typeof control.value === 'string' ? parseFloat(control.value.replace(/\s/g, '')) : control.value;
			const isFirstSharedProperty = form.get('propertyData.firstSharedProperty')?.value;
			const min = 1_000_000;
			let max = 150_000_000;
			const propertyType = form.controls.propertyData?.controls.type.value as PropertyType;

			const maxLimitOfPropertyType: Partial<Record<PropertyType, number>> = {
				[PropertyType.Apartment]: 100_000_000,
				[PropertyType.FamilyHouse]: 150_000_000,
				[PropertyType.SemiDetachedHouse]: 150_000_000,
				[PropertyType.Townhouse]: 150_000_000,
			};
			const fail = () => ({ purchasePriceOfPlannedProperty: { productTypes, value: max } });
			const failMin = () => ({ minMaxNumber: { productTypes, min, max } });

			if (
				(productTypes.includes(ProductType.CsokPlusz) && isFirstSharedProperty === true) ||
				productTypes.includes(ProductType.OtthonStart)
			) {
				max = maxLimitOfPropertyType[propertyType] ?? max;
			}

			if (value > max) {
				return fail();
			}

			if (value < min) {
				return failMin();
			}

			return null;
		},

	BirthDatesOfChildren:
		(productTypes: ProductType[], form: AvailableCalculatorControls | DynamicForm<WebFormFormGroup>) =>
		(control: AbstractControl) => {
			const isWebform: boolean = !Object.keys(form.controls).some((e) => e === 'familyStatus');
			let formRoot: AvailableCalculatorControls | DynamicForm<WebFormFormGroup>;
			let currentChildrenCount: number;
			const eachDatesHasValue: boolean = control.value.every((date: string) => date !== '');
			if (isWebform) {
				formRoot = form as DynamicForm<WebFormFormGroup>;
				currentChildrenCount = formRoot.get('household.numberOfChildren')?.value ?? 0;
			} else {
				formRoot = form as AvailableCalculatorControls;
				currentChildrenCount = formRoot.get('familyStatus.numberOfChildren')?.value ?? 0;
			}
			if (currentChildrenCount !== control.value.length || !eachDatesHasValue) {
				return { birthDatesOfChildren: { productTypes, currentChildrenCount } };
			}
			return null;
		},

	MonthlyNetIncome:
		(productTypes: ProductType[], form: AvailableCalculatorControls) => (control: AbstractControl) => {
			const incomeTypesWithMonthlyNetIncome = [...incomeTypes, 'monthlyNetIncome'];
			const partnerIncomeTypesWithMonthlyNetIncome = [...partnerIncomeTypes, 'partnerNetMonthlyIncome'];

			const everyIncomeIsZero = incomeTypesWithMonthlyNetIncome.every((key) => {
				const value = parseFormattedNumber(form.get(`incomeAndEducation.${key}`)?.value as unknown as string);
				return !value;
			});
			const everyPartnerIncomeIsZero = partnerIncomeTypesWithMonthlyNetIncome.every((key) => {
				const value = parseFormattedNumber(form.get(`incomeAndEducation.${key}`)?.value as unknown as string);
				return !value;
			});
			const maritalStatus = form.controls.familyStatus?.controls.maritalStatus.value;
			const isCouple =
				maritalStatus === MaritalStatus.Married || maritalStatus === MaritalStatus.CivilPartnership;
			const isValueNull = control.value === null;
			return everyIncomeIsZero && everyPartnerIncomeIsZero
				? { monthlyNetIncome: { isValueNull, isCouple } }
				: null;
		},

	// just implemented, not needed
	Disabled: () => () => {
		return null;
	},

	LocationRequired:
		(productTypes: ProductType[], form: AvailableCalculatorControls) => (control: AbstractControl) => {
			const isPropertyLocationRequired =
				form.get('propertyData.propertyLocationQuestion')?.value === PropertyLocationQuestion.Known &&
				!control.value;

			return isPropertyLocationRequired ? { propertyLocationRequired: { productTypes } } : null;
		},

	WebformCustomersMarried:
		(productTypes: ProductType[], form: DynamicForm<WebFormFormGroup>) => (control: AbstractControl) => {
			const customers = form.controls.customers.value;

			if (customers.length < 2) {
				return null;
			}

			if (customers[0]?.type !== CustomerType.Husband || customers[1]?.type !== CustomerType.Wife) {
				return { webformCustomersMarried: { productTypes } };
			}

			return null;
		},

	MinMaxChar:
		(productTypes: ProductType[], form: AvailableCalculatorControls, min: number, max: number) =>
		(control: AbstractControl<string | null>) => {
			const value = control.value;
			if (value !== null && value !== undefined && value !== '' && (value.length < min || value.length > max)) {
				return { minMaxChar: { productTypes, min, max } };
			}
			return null;
		},

	WebformLanguageLevel:
		(productTypes: ProductType[], form: WebformDto) => (control: AbstractControl<LanguageSkillLevel>) => {
			const languageSkillLevel = control.value;
			const numberOfBasicLanguageExams = control.parent?.get('numberOfBasicLanguageExams')?.value;
			const numberOfIntermediateLanguageExams = control.parent?.get('numberOfIntermediateLanguageExams')?.value;
			const numberOfAdvancedLanguageExams = control.parent?.get('numberOfAdvancedLanguageExams')?.value;

			if (
				(languageSkillLevel == LanguageSkillLevel.None && numberOfAdvancedLanguageExams > 0) ||
				(languageSkillLevel == LanguageSkillLevel.None && numberOfIntermediateLanguageExams > 0) ||
				(languageSkillLevel == LanguageSkillLevel.None && numberOfBasicLanguageExams > 0)
			) {
				return { webformLanguageLevel: { productTypes } };
			}

			if (
				(languageSkillLevel == LanguageSkillLevel.Basic && numberOfAdvancedLanguageExams > 0) ||
				(languageSkillLevel == LanguageSkillLevel.Basic && numberOfIntermediateLanguageExams > 0)
			) {
				return { webformLanguageLevel: { productTypes } };
			}

			if (languageSkillLevel == LanguageSkillLevel.Basic && numberOfBasicLanguageExams == 0) {
				return { webformLanguageLevel: { productTypes } };
			}

			if (languageSkillLevel == LanguageSkillLevel.Intermediate && numberOfAdvancedLanguageExams > 0) {
				return { webformLanguageLevel: { productTypes } };
			}

			if (languageSkillLevel == LanguageSkillLevel.Intermediate && numberOfIntermediateLanguageExams == 0) {
				return { webformLanguageLevel: { productTypes } };
			}

			if (languageSkillLevel == LanguageSkillLevel.Advanced && numberOfAdvancedLanguageExams == 0) {
				return { webformLanguageLevel: { productTypes } };
			}

			return null;
		},

	// only used as an ancestor class at backend
	RegexPattern: () => () => {
		return null;
	},

	CalculatorCsokPlusLoanWifeMinMaxAge:
		(productTypes: ProductType[], form: DynamicForm<WebFormFormGroup>, minAge: number, maxAge: number) =>
		(control: AbstractControl<string | null>) => {
			if (!control.value) {
				return null;
			}
			let localMaxAge = maxAge;
			const age = differenceInYears(new Date(), new Date(control.value));
			if (control.parent?.get('atLeast12weeksPregnant')?.value) {
				localMaxAge = 69;
			}
			return age < minAge || age >= localMaxAge ? { minMaxAge: { minAge, maxAge: localMaxAge } } : null;
		},

	MinEmbryoCountIfWifeIsOlderThan:
		(productTypes: ProductType[], form: DynamicForm<WebFormFormGroup>, wifeAge: number) =>
		(control: AbstractControl<string | null>) => {
			if (control.value == null || form.controls.customers.length < 2) {
				return null;
			}
			const partnerBirthDate = form.controls.customers.at(1).controls.personalData.controls.birthDate.value;
			if (!partnerBirthDate) {
				return null;
			}
			const partnerAge = differenceInYears(new Date(), new Date(partnerBirthDate));
			if (partnerAge > wifeAge && parseInt(control.value) < 1) {
				return { minEmbryoCount: { productTypes } };
			}
			return null;
		},

	DealCsokPlusLoanWifeMinMaxAge:
		(productTypes: ProductType[], form: DynamicForm<WebFormFormGroup>, minAge: number, maxAge: number) =>
		(control: AbstractControl<string | null>) => {
			if (!control.value) {
				return null;
			}
			let localMaxAge = maxAge;
			const age = differenceInYears(new Date(), new Date(control.value));
			if (control.parent?.parent?.get('type')?.value === CustomerType.Wife) {
				if (
					form.controls.loanDetails.controls?.atLeast12weeksFetusesNumbers?.value &&
					form.controls.loanDetails.controls.atLeast12weeksFetusesNumbers.value > 0
				) {
					localMaxAge = 69;
				}
				return age < minAge || age >= localMaxAge ? { minMaxAge: { minAge, maxAge: localMaxAge } } : null;
			}
			return null;
		},

	CustomerTypeMinMaxAge:
		(
			productTypes: ProductType[],
			form: DynamicForm<WebFormFormGroup>,
			expectedCustomerType: CustomerType,
			minAge: number,
			maxAge: number
		) =>
		(control: AbstractControl<string | null>) => {
			if (!control.value) {
				return null;
			}
			const age = differenceInYears(new Date(), new Date(control.value));
			if (control.parent?.parent?.get('type')?.value === expectedCustomerType) {
				return age < minAge || age >= maxAge ? { minMaxAge: { minAge, maxAge } } : null;
			}
			return null;
		},

	WebformIdDocumentNumber:
		(productTypes: ProductType[], form: DynamicForm<WebFormFormGroup>) =>
		(control: AbstractControl<string | null>) => {
			const idNumber = control.value;
			const idCardType = control.parent?.get('idDocumentType')?.value;

			if (!idNumber || !idCardType) {
				return null;
			}

			switch (idCardType) {
				case IdCardType.IdCard:
					return valueMatches(idNumber, idCardPattern) ? null : { webformIdCardPattern: { productTypes } };
				case IdCardType.DrivingLicense:
					return valueMatches(idNumber, drivingLicencePattern)
						? null
						: { webformDrivingLicensePattern: { productTypes } };
				case IdCardType.Passport:
					return valueMatches(idNumber, passportPattern)
						? null
						: { webformPassportPattern: { productTypes } };
				case IdCardType.ForeignIdDocument:
					return valueMatches(idNumber, foreignDocumentPattern)
						? null
						: { foreignIdDocument: { productTypes } };
				default:
					return null;
			}
		},

	RequiredIfPublicSpaceTypePopulated:
		(productTypes: ProductType[], form: DynamicForm<WebFormFormGroup>) =>
		(control: AbstractControl<number | string | null>) => {
			const publicSpaceType = control.parent?.get('publicSpaceType')?.value;

			if (publicSpaceType !== PublicSpaceType.Notpopulated) {
				return Validators.required(control) ? { required: { productTypes } } : null;
			}
			return null;
		},

	RequiredIfPublicSpaceTypeNotPopulated:
		(productTypes: ProductType[], form: DynamicForm<WebFormFormGroup>) =>
		(control: AbstractControl<number | string | null>) => {
			const publicSpaceType = control.parent?.get('publicSpaceType')?.value;

			if (publicSpaceType === PublicSpaceType.Notpopulated) {
				return Validators.required(control) ? { required: { productTypes } } : null;
			}

			return null;
		},

	// not used at backend
	RequiredIf:
		(
			productTypes: ProductType[],
			form: AvailableCalculatorControls | DynamicForm<WebFormFormGroup>,
			...params: string[]
		) =>
		(control: AbstractControl<number | string>) => {
			const isRequired = Validators.required(control);

			if (!isRequired) {
				return null;
			}

			const value = control.value as unknown;
			const acceptedValues = params.slice(1) as unknown[];

			if (acceptedValues.includes(value)) {
				return Validators.required(control) ? { required: { productTypes } } : null;
			}

			return null;
		},

	// todo: this validator is on the incomeAndEducation form group, show it accordingly
	WebformMonthlyNetIncome:
		(productTypes: ProductType[], form: DynamicForm<WebFormFormGroup>) =>
		(control: AbstractControl<number | null>) => {
			const customers = form.controls.customers;
			const maritalStatus =
				form.controls.customers.controls[0].controls.personalData.controls.maritalStatus.value;
			const sliceEndIndex =
				maritalStatus === MaritalStatus.Married || maritalStatus === MaritalStatus.CivilPartnership ? 2 : 1;

			const customersIncome = customers.controls.map((customer) => {
				return incomeTypes
					.map((incomeType) =>
						parseFormattedNumber(
							customer.get(`incomeAndEducation.${incomeType}`)?.value as unknown as string
						)
					)
					.reduce((accumulator, currentValue) => accumulator + currentValue);
			});

			if (customersIncome.slice(0, sliceEndIndex).every((income) => income === 0)) {
				return { webformMonthlyNetIncome: { productTypes } };
			}

			if (customersIncome.slice(sliceEndIndex).some((income) => income === 0)) {
				return { webformMonthlyNetIncome: { productTypes } };
			}

			return null;
		},

	WebformCustomerZeroMaritalStatus:
		(productTypes: ProductType[], form: DynamicForm<WebFormFormGroup>) =>
		(control: AbstractControl<MaritalStatus | null>) => {
			const maritalStatus = control.value;
			const customers = form.controls.customers.value;

			if (!maritalStatus) {
				return null;
			}

			if (customers.length > 2) {
				const customersAboveTwoAreDebtPartners = customers
					.slice(2)
					.every((customer) => customer.type === CustomerType.DebtPartner);
				if (!customersAboveTwoAreDebtPartners) {
					return { webformCustomerZeroMaritalStatus: { productTypes } };
				}
			}

			if (maritalStatus == MaritalStatus.Married) {
				if (customers.length < 2) {
					return { webformCustomerZeroMaritalStatus: { productTypes } };
				}

				if (customers[0].type !== CustomerType.Husband || customers[1]?.type !== CustomerType.Wife) {
					return { webformCustomerZeroMaritalStatus: { productTypes } };
				}

				if (customers[1]?.personalData?.maritalStatus !== MaritalStatus.Married) {
					return { webformCustomerZeroMaritalStatus: { productTypes } };
				}
			}

			return null;
		},

	RegularIncome:
		(productTypes: ProductType[], form: DynamicForm<WebFormFormGroup>) =>
		(control: AbstractControl<string | null>) => {
			let employeeIncome = control.parent?.get('employeeIncome')?.value;
			let familySupport = control.parent?.get('familySupport')?.value;

			if (typeof employeeIncome === 'string') {
				employeeIncome = parseFormattedNumber(employeeIncome);
			}

			if (typeof familySupport === 'string') {
				familySupport = parseFormattedNumber(familySupport);
			}

			if (employeeIncome > 0 || familySupport > 0) {
				return Validators.required(control) ? { required: { productTypes } } : null;
			}

			return null;
		},

	PropertyDataTypePropertyGoal:
		(productTypes: ProductType[], form: DynamicForm<WebFormFormGroup>) =>
		(control: AbstractControl<PropertyType | null>) => {
			const propertyType = control.value;

			if (
				propertyType === PropertyType.WeekendHouse ||
				propertyType === PropertyType.HolidayHome ||
				propertyType === PropertyType.Garage
			) {
				return { propertyDataTypePropertyGoal: { productTypes } };
			}

			return null;
		},

	GoalOfLoanPropertyGoal:
		(productTypes: ProductType[], form: DynamicForm<WebFormFormGroup>, ...disallowedGoalOfLoans: string[]) =>
		(control: AbstractControl<GoalOfLoan | null>) => {
			const goalOfLoan = control.value;

			const disallowedValues = disallowedGoalOfLoans as GoalOfLoan[];

			if (goalOfLoan && disallowedValues.includes(goalOfLoan)) {
				return { goalOfLoanPropertyGoal: { goalOfLoan, productTypes } };
			}

			return null;
		},

	GoalOfPropertyValidator:
		(productTypes: ProductType[], form: DynamicForm<WebFormFormGroup>) =>
		(control: AbstractControl<GoalOfProperty | null>) => {
			const goalOfProperty = control.value;
			if (productTypes.includes(ProductType.CsokPlusz)) {
				if (goalOfProperty === GoalOfProperty.OwnerWillRent) {
					return {
						goalOfProperty: {
							value: GoalOfProperty.OwnerWillRent,
							productType: ProductType.CsokPlusz,
						},
					};
				}
			}

			return null;
		},

	DisallowedGoalOfApplication:
		(
			productTypes: ProductType[],
			form: AvailableCalculatorControls | DynamicForm<WebFormFormGroup>,
			...disallowedGoalOfApplications: string[]
		) =>
		(control: AbstractControl<GoalOfApplication | null>) => {
			if (control.value == null) {
				return null;
			}

			const goalOfApplication = control.value;
			const disallowedValues = disallowedGoalOfApplications as GoalOfApplication[];

			if (disallowedValues.includes(goalOfApplication)) {
				return {
					disallowedGoalOfApplication: {
						goalOfApplication,
						productTypes,
					},
				};
			}

			return null;
		},

	WebformPhoneUnique:
		(productTypes: ProductType[], form: DynamicForm<WebFormFormGroup>) =>
		(control: AbstractControl<string | null>) => {
			const phoneNumbers = form.controls.customers.controls
				.filter((customer) => customer.controls.personalData.controls.phone.getRawValue() !== null)
				.map((customer) => customer.controls.personalData.controls.phone.getRawValue());

			if (!phoneNumbers) {
				return null;
			}

			return new Set(phoneNumbers).size !== phoneNumbers.length ? { webformPhoneUnique: { productTypes } } : null;
		},

	WebformEmailUnique:
		(productTypes: ProductType[], form: DynamicForm<WebFormFormGroup>) =>
		(control: AbstractControl<string | null>) => {
			const emails = form.controls.customers.controls
				.filter((customer) => customer.controls.personalData.controls.email.getRawValue() !== null)
				.map((customer) => customer.controls.personalData.controls.email.getRawValue());

			if (!emails) {
				return null;
			}

			return new Set(emails).size !== emails.length ? { webformEmailUnique: { productTypes } } : null;
		},

	SoldSharedPropertiesPrice:
		(productTypes: ProductType[], form: DynamicForm<WebFormFormGroup>) =>
		(control: AbstractControl<number | null>) => {
			let sum = 0;
			const formArray = form.controls.propertyData.controls.soldSharedProperties;
			if (!formArray) {
				return null;
			}

			formArray.controls.forEach((gr) => {
				sum += parseFormattedNumber(gr.controls.soldSharedPropertyPrice.value ?? 0);
			});
			const purchasePrice = parseFormattedNumber(control.value ?? 0) * 1_000_000;

			return sum > purchasePrice ? { soldSharedPropertyPrice: { productTypes } } : null;
		},

	MinMaxFloat:
		(
			productTypes: ProductType[],
			form: AvailableCalculatorControls | DynamicForm<WebFormFormGroup>,
			min: string,
			max: string
		) =>
		(control: AbstractControl<number | string | null>) => {
			const value = typeof control.value === 'string' ? parseFormattedNumber(control.value) : control.value;

			if (
				value !== null &&
				control.value !== '' &&
				(isNaN(value) || value < parseFloat(min) || value > parseFloat(max))
			) {
				return { minMaxFloat: { productTypes, min, max } };
			}
			return null;
		},

	MinMaxMonths,

	AdditionalHouseHoldMembers:
		(productTypes: ProductType[], form: AvailableCalculatorControls | DynamicForm<WebFormFormGroup>) =>
		(control: AbstractControl<number | null>) => {
			const hasAdditionalHouseholdMembers = control.value;
			const numberOfPensioners = control.parent?.get('numberOfPensioners')?.value;
			const numberOfAdults = control.parent?.get('numberOfAdults')?.value;

			if (!hasAdditionalHouseholdMembers) {
				return null;
			}

			if (numberOfPensioners === null || numberOfAdults === null) {
				return null;
			}

			if (numberOfPensioners || numberOfAdults) {
				return null;
			}

			return { additionalHouseHoldMembers: { productTypes } };
		},

	WebformOtherPartnerStatus:
		(productTypes: ProductType[], form: DynamicForm<WebFormFormGroup>) =>
		(control: AbstractControl<MaritalStatus | null>) => {
			const maritalStatus = control.value;

			if (!maritalStatus) {
				return null;
			}

			const customers = form.controls.customers.value;

			const parentControls = control.parent?.parent?.controls as {
				[key: string]: AbstractControl<unknown, unknown>;
			};
			const currentCustomerId = parentControls?.['customerId']?.value as string;

			const currentCustomerIndex: number = customers.findIndex(
				(customer) => customer.customerId === currentCustomerId
			);

			const singleStatuses = new Set([MaritalStatus.Single, MaritalStatus.Divorced, MaritalStatus.Widowed]);

			if (customers.length > 2) {
				const customersAboveTwoAreDebtPartners = customers
					.slice(2)
					.every((customer) => customer.type === CustomerType.DebtPartner);
				if (!customersAboveTwoAreDebtPartners) {
					return { webformOtherPartnerStatus: { productTypes } };
				}
			}

			if (currentCustomerIndex > 1) {
				return null;
			}

			if (singleStatuses.has(maritalStatus)) {
				if (customers[0]?.type !== CustomerType.Debtor) {
					return { webformOtherPartnerStatus: { productTypes } };
				}

				if (customers.length > 1 && customers[1]?.type !== CustomerType.DebtPartner) {
					return { webformOtherPartnerStatus: { productTypes } };
				}
			}

			if (maritalStatus === MaritalStatus.CivilPartnership) {
				if (customers.length < 2) {
					return { webformOtherPartnerStatus: { productTypes } };
				}

				if (customers[0]?.type !== CustomerType.Debtor || customers[1]?.type !== CustomerType.Partner) {
					return { webformOtherPartnerStatus: { productTypes } };
				}

				if (customers[1]?.personalData?.maritalStatus !== MaritalStatus.CivilPartnership) {
					return { webformOtherPartnerStatus: { productTypes } };
				}
			}

			return null;
		},

	BabaAtLeast12weeksPregnant:
		(productTypes: ProductType[], form: AvailableCalculatorControls) =>
		(control: AbstractControl<number | string | null>) => {
			if (!control.value) {
				return null;
			}
			const age = differenceInYears(new Date(), new Date(control.value));
			const atLeast12WeeksPregnant = control.parent?.get('atLeast12weeksPregnant')?.value;

			let maxAge;
			if (!atLeast12WeeksPregnant) {
				if (productTypes.includes(ProductType.CsokPlusz)) {
					maxAge = 40;
				}

				if (productTypes.includes(ProductType.Babavaro)) {
					maxAge = 29;
				}

				if (!maxAge) {
					return null;
				}

				return age >= maxAge ? { babaAtLeast12weeksPregnant: { productTypes } } : null;
			}

			return null;
		},

	// TODO: rendesen megcsinálni
	MinMaxAgeWithAtLeast12weeksPregnant:
		(productTypes: ProductType[], form: AvailableCalculatorControls) =>
		(control: AbstractControl<number | string | null>) => {
			if (!control.value || !productTypes.includes(ProductType.Babavaro)) {
				return null;
			}
			const age = differenceInYears(new Date(), new Date(control.value));

			const minAge = 23;
			const maxAge = 35;

			const validAge = minAge <= age && age <= maxAge;
			return validAge
				? null
				: {
						minMaxAgeWithAtLeast12weeksPregnant: {
							minAge,
							maxAge,
							minNonPregnantAge: -1,
							maxNonPregnantAge: -1,
						},
					};
		},

	EmployerTaxNumberValidator:
		(productTypes: ProductType[], form: DynamicForm<WebFormFormGroup>) =>
		(control: AbstractControl<string | null>) => {
			if (!control.value) {
				return null;
			}
			return valueMatches(control.value, companyTaxIdPattern)
				? null
				: { employerTaxNumberValidator: { productTypes } };
		},

	EmailValidator,

	IdNumberValidator:
		(productTypes: ProductType[], form: DynamicForm<WebFormFormGroup>) =>
		(control: AbstractControl<string | null>) => {
			if (!control.value) {
				return null;
			}

			return valueMatches(control.value, idNumberPattern) ? null : { idNumberValidator: { productTypes } };
		},

	PersonalIdNumberValidator:
		(productTypes: ProductType[], form: DynamicForm<WebFormFormGroup>) =>
		(control: AbstractControl<string | null>) => {
			if (!control.value) {
				return null;
			}

			return valueMatches(control.value, personalIdPattern)
				? null
				: { personalIdNumberValidator: { productTypes } };
		},

	PhoneNumberValidator:
		(productTypes: ProductType[], form: DynamicForm<WebFormFormGroup>) =>
		(control: AbstractControl<number | string | null>) => {
			if (!control.value) {
				return null;
			}

			return valueMatches(control.value.toString(), mobilePhoneWithPrefixPattern)
				? null
				: { phoneNumberValidator: { productTypes } };
		},

	ResidenceCardNumberValidator:
		(productTypes: ProductType[], form: DynamicForm<WebFormFormGroup>) =>
		(control: AbstractControl<string | null>) => {
			if (!control.value) {
				return null;
			}

			return valueMatches(control.value, idCardPattern)
				? null
				: { residenceCardNumberValidator: { productTypes } };
		},

	IsPurchasePriceOfPlannedPropertyKnown:
		(productTypes: ProductType[], form: DynamicForm<WebFormFormGroup>, min: string, max: string) =>
		(control: AbstractControl<string | null>) => {
			if (control.value === null) {
				return null;
			}

			const isPurchasePriceKnown = control.parent?.get('isPurchasePriceOfPlannedPropertyKnown')?.value;

			let newMin = min;
			let newMax = max;

			if (isPurchasePriceKnown === false) {
				newMin = '0';
				newMax = '0';
			}

			return AppValidators.MinMaxFloat(productTypes, form, newMin, newMax)(control);
		},

	RequiredTrueIfRepaymentSuspensionRequested:
		(productTypes: ProductType[], form: AvailableCalculatorControls) =>
		(control: AbstractControl<string | null>) => {
			const repaymentSuspensionRequested = form.get('loanDetails.babavaro36MonthsRepaymentSuspension')?.value;
			const value = control.value;

			return repaymentSuspensionRequested && !value
				? { requiredTrueIfRepaymentSuspensionRequested: { productTypes } }
				: null;
		},

	ChildrenBornFromPreviousSubsidisedLoan:
		(productTypes: ProductType[], form: AvailableCalculatorControls) =>
		(control: AbstractControl<string | null>) => {
			const childrenBornFromPreviousSubsidisedLoan = form.get(
				'familyStatus.childrenBornFromPreviousSubsidisedLoan'
			)?.value;
			const numberOfChildren = parseFormattedNumber(control.value ?? 0);

			return childrenBornFromPreviousSubsidisedLoan && numberOfChildren === 0
				? { childrenBornFromPreviousSubsidisedLoan: { productTypes } }
				: null;
		},

	// KO validators

	CsokAndInterestSupportRepaidKO: (productTypes: ProductType[]) => (control: AbstractControl<boolean | null>) => {
		if (control.value === false) {
			return { csokAndInterestSupportRepaidKO: { KO: true, productTypes } };
		}
		return null;
	},

	AreBothOwnersAtPurchaseKO: (productTypes: ProductType[]) => (control: AbstractControl<boolean | null>) => {
		if (control.value === false) {
			return { areBothOwnersAtPurchaseKO: { KO: true, productTypes } };
		}
		return null;
	},

	IsPropertySizeGreaterThanSpecifiedSizeKO:
		(productTypes: ProductType[], form: AvailableCalculatorControls) =>
		(control: AbstractControl<boolean | null>) => {
			const goal = form.controls.propertyData?.controls.goalOfApplication.value;
			const numberOfChildren = form.controls.familyStatus?.controls.numberOfChildren.value;
			const numberOfExpectedChildren = form.controls.familyStatus?.controls.numberOfExpectedChildren.value;
			const squareMeters = countRequiredSquareMeters(goal, numberOfChildren, numberOfExpectedChildren);

			const allChildrenCount = (numberOfChildren ?? 0) + (numberOfExpectedChildren ?? 0);
			if (control.value === false) {
				return {
					isPropertySizeGreaterThanSpecifiedSizeKO: {
						KO: true,
						productTypes,
						allChildrenCount,
						squareMeters,
					},
				};
			}
			return null;
		},

	ChildrenBornFromPreviousSubsidisedLoanKO:
		(productTypes: ProductType[]) => (control: AbstractControl<boolean | null>) => {
			if (control.value === false) {
				return {
					childrenBornFromPreviousSubsidisedLoanKO: { KO: true, productTypes },
				};
			}
			return null;
		},

	MaritalStatusMarriedKO: (productTypes: ProductType[]) => (control: AbstractControl<MaritalStatus | null>) => {
		if (control.value !== MaritalStatus.Married) {
			return { maritalStatusMarriedKO: { KO: true, productTypes } };
		}
		return null;
	},

	AtLeastTwoYearsOfSocialInsuranceKO:
		(productTypes: ProductType[], form: AvailableCalculatorControls) =>
		(control: AbstractControl<boolean | null>) => {
			const maritalStatus = form.controls.familyStatus?.controls.maritalStatus.value;
			const hasPartner = maritalStatus ? maritalStatus !== MaritalStatus.Single : false;

			if (control.value === false) {
				return { atLeastTwoYearsOfSocialInsuranceKO: { KO: true, productTypes, hasPartner: hasPartner } };
			}
			return null;
		},

	RequiredTrueKO: (productTypes: ProductType[]) => (control: AbstractControl) => {
		return Validators.requiredTrue(control) ? { requiredTrueKO: { KO: true, productTypes } } : null;
	},

	RequiredFalseKO: (productTypes: ProductType[], form: AvailableCalculatorControls) => (control: AbstractControl) => {
		if (control.value === true) {
			const controlPath = getControlPath(control);
			const translationPrefix = controlPath || undefined;
			return {
				requiredFalseKO: {
					KO: true,
					productTypes,
					translationPrefix,
				},
			};
		}
		return null;
	},

	SpouseOrPartnerKO: (productTypes: ProductType[]) => (control: AbstractControl) => {
		return control.value === MaritalStatus.Single ? { spouseOrPartnerKo: { KO: true, productTypes } } : null;
	},

	PropertyLocationQuestionKO:
		(productTypes: ProductType[]) => (control: AbstractControl<PropertyLocationQuestion | null>) => {
			const propertyLocationKO = control.value === PropertyLocationQuestion.NotInList;

			return propertyLocationKO ? { propertyLocationKO: { KO: true, productTypes } } : null;
		},

	TaxIdNumberValidator:
		(productTypes: ProductType[], form: DynamicForm<WebFormFormGroup>) =>
		(control: AbstractControl<string | null>) => {
			if (!control.value) {
				return null;
			}
			const value = control.value;

			if (typeof value !== 'string' || !/^8\d{9}$/.test(value)) {
				return { invalidTaxNumber: true };
			}

			const digits = value.split('').map(Number);
			let sum = 0;

			for (let i = 0; i < 9; i++) {
				sum += digits[i] * (i + 1);
			}

			const calculatedCheckDigit = sum % 11;
			const checkDigit = digits[9];

			return checkDigit === calculatedCheckDigit ? null : { invalidTaxNumber: { productTypes } };
		},

	MinMaxFetusNumberWhenRepaymentSuspended:
		(
			productTypes: ProductType[],
			form: AvailableCalculatorControls,
			minWhenRepaymentNotSuspended: string,
			maxWhenRepaymentNotSuspended: string,
			minWhenRepaymentSuspended: string,
			maxWhenRepaymentSuspended: string,
			repaymentSuspendedFieldName: string
		) =>
		(control: AbstractControl<number | string | null>) => {
			const value = typeof control.value === 'string' ? parseFormattedNumber(control.value) : control.value;
			const fieldName = repaymentSuspendedFieldName.split('.').pop();
			if (!fieldName) {
				return null;
			}
			const repaymentSuspended = control.parent?.get(fieldName)?.value;
			let min, max;
			if (repaymentSuspended) {
				min = minWhenRepaymentSuspended;
				max = maxWhenRepaymentSuspended;
			} else {
				min = minWhenRepaymentNotSuspended;
				max = maxWhenRepaymentNotSuspended;
			}
			if (
				value !== null &&
				value !== undefined &&
				control.value !== '' &&
				(isNaN(value) || value < parseInt(min) || value > parseInt(max))
			) {
				return { minMaxNumber: { productTypes, min, max } };
			}
			return null;
		},

	BeneficialInterestValidator: (productTypes: ProductType[]) => (control: AbstractControl<boolean | null>) => {
		if (control.value === true && productTypes.includes(ProductType.OtthonStart)) {
			return { beneficialInterestValidator: { productTypes } };
		}

		return null;
	},
};

export const debouncePendingValidator =
	(debounceMs = 1000) =>
	(control: AbstractControl): Observable<ValidationErrors | null> => {
		return timer(debounceMs).pipe(map(() => null));
	};

// helper methods
function valueMatches(value: string, pattern: RegExp) {
	return value !== '' && !pattern.test(value) ? false : true;
}

/**
 * Utility function to get the control path by traversing up the form hierarchy
 * @param control - The AbstractControl to get the path for
 * @returns string - The dot-separated path to the control (e.g., 'familyStatus.isMarried')
 */
function getControlPath(control: AbstractControl): string {
	const path: string[] = [];
	let currentControl: AbstractControl | null = control;

	while (currentControl && currentControl.parent) {
		// Find the key of this control in its parent
		const parent = currentControl.parent as FormGroup | FormArray;

		if (parent instanceof FormGroup) {
			// For FormGroup, find the key that matches this control
			const key = Object.keys(parent.controls).find((k) => parent.controls[k] === currentControl);
			if (key) {
				path.unshift(key);
			}
		} else if (parent instanceof FormArray) {
			// For FormArray, find the index
			const index = parent.controls.findIndex((c) => c === currentControl);
			if (index !== -1) {
				path.unshift(index.toString());
			}
		}

		currentControl = parent;
	}

	return path.join('.');
}
